<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { type Plan, SchemeType } from '@/shared/types/api.types'
  import { toFixedNumber } from '@/shared/utils'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'

  interface Props {
    plan: Plan
    travelersCount: number
  }

  defineProps<Props>()

  const { t } = useI18n()

  const coverageOptions = [
    {
      title: t('outbound.select_plan.insurance_values.category_1.label'),
      description: t('outbound.select_plan.insurance_values.category_1.quote_desc'),
    },
    {
      title: t('outbound.select_plan.insurance_values.category_2.label'),
      description: t('outbound.select_plan.insurance_values.category_2.quote_desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      title: t('outbound.select_plan.insurance_values.category_3.label'),
      description: t('outbound.select_plan.insurance_values.category_3.quote_desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
  ]
</script>

<template>
  <FlowCardQC class="quote-coverage-qc">
    <WrapperQC direction="column">
      <div class="quote-coverage-qc__header">
        <h2 class="heading-5">{{ $t('travel.common.quote.inclusive_cover.name') }}</h2>
      </div>

      <div class="quote-coverage-qc__content">
        <template v-for="{ title, description, notAvailableFor } in coverageOptions" :key="title">
          <WrapperQC
            v-if="!notAvailableFor?.includes(plan.schemeTypeCode)"
            gap="var(--spacing-5)"
            class="quote-coverage-qc__item"
          >
            <div class="quote-coverage-qc__icon-wrapper">
              <div class="quote-coverage-qc__icon">
                <IconQC name="check" color="var(--colorIcnSrfAction)" />
              </div>
            </div>

            <WrapperQC class="quote-coverage-qc__texts" direction="column" gap="var(--spacing-2)">
              <h3 class="quote-coverage-qc__title text-medium">{{ title }}</h3>
              <p class="quote-coverage-qc__description text-regular">{{ description }}</p>
            </WrapperQC>
          </WrapperQC>
        </template>
      </div>

      <WrapperQC class="quote-coverage-qc__footer" direction="column">
        <p class="quote-coverage-qc__price ltr paragraph-default-bold">
          {{ `${$t('qar')} ${toFixedNumber(plan?.price ?? 0)}` }}
        </p>
        <span v-if="travelersCount > 1" class="quote-coverage-qc__price-desc text-small-regular">
          {{ $t('travel.common.quote_price_desc.name', { count: travelersCount }) }}
        </span>
      </WrapperQC>
    </WrapperQC>
  </FlowCardQC>
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/mixins';

  .quote-coverage-qc {
    @include mixins.mobile-flow {
      padding-block: var(--spacing-8);
    }
  }

  .quote-coverage-qc__header {
    padding-bottom: var(--spacing-2);
  }

  .quote-coverage-qc__content {
    padding-block: var(--spacing-6);
    border-bottom: 1px solid var(--colorBrdrSrfSecondary);
  }

  .quote-coverage-qc__icon-wrapper {
    display: flex;
    height: fit-content;
    padding-block: var(--spacing-1);
  }

  .quote-coverage-qc__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;

    /* qic-connect-ignore-next-line */
    border-radius: 50%;
    background-color: var(--colorBgSrfTertiaryAlpha);
    flex-shrink: 0;
  }

  .quote-coverage-qc__item:not(:first-child),
  .quote-coverage-qc__footer {
    padding-top: var(--spacing-6);
  }

  .quote-coverage-qc__footer {
    align-items: start;
  }

  .quote-coverage-qc__description,
  .quote-coverage-qc__price-desc {
    color: var(--colorTxtSrfSecondary);
  }
</style>
